"use client";

import { useAppStore, type ViewName } from "@/lib/store";
import { useI18n } from "@/lib/i18n/provider";
import { isStaffRole } from "@/components/booking/format";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Home,
  CalendarCheck,
  Building2,
  Shield,
  ShieldCheck,
  Receipt,
  PieChart,
  Bell,
  BookOpen,
  ArrowRight,
} from "lucide-react";

// The Home screen IS the menu: a clean card grid of destinations, gated by
// role. Level 0 of the one-page, multi-level navigation.
export function HomeView() {
  const { user, authLoading, openArea, openAuth, impersonating } = useAppStore();
  const { t } = useI18n();

  if (authLoading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  const isStaff = !!user && isStaffRole(user.role);
  const isOwnerOrAdmin = !!user && (user.role === "OWNER" || user.role === "ADMIN");

  const cards: {
    view: ViewName | "docs";
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    hint: string;
    badge?: string;
  }[] = [
    {
      view: "browse",
      icon: Home,
      label: t("home.browse"),
      hint: t("home.browseHint"),
    },
    ...(user
      ? [
          {
            view: "my-reservations" as ViewName,
            icon: CalendarCheck,
            label: t("home.myTrips"),
            hint: t("home.myTripsHint"),
          },
        ]
      : []),
    ...(isOwnerOrAdmin
      ? [
          {
            view: "host" as ViewName,
            icon: Building2,
            label: t("home.host"),
            hint: t("home.hostHint"),
          },
        ]
      : []),
    ...(isStaff
      ? [
          {
            view: "moderator" as ViewName,
            icon: ShieldCheck,
            label: t("home.moderator"),
            hint: t("home.moderatorHint"),
            badge: t("home.reviewBadge"),
          },
          {
            view: "expenses" as ViewName,
            icon: Receipt,
            label: t("home.expenses"),
            hint: t("home.expensesHint"),
          },
          {
            view: "dashboard" as ViewName,
            icon: PieChart,
            label: t("home.dashboard"),
            hint: t("home.dashboardHint"),
          },
          {
            view: "notifications" as ViewName,
            icon: Bell,
            label: t("home.notifications"),
            hint: t("home.notificationsHint"),
          },
        ]
      : []),
    ...(user && user.role === "ADMIN"
      ? [
          {
            view: "admin" as ViewName,
            icon: Shield,
            label: t("home.admin"),
            hint: t("home.adminHint"),
          },
        ]
      : []),
    {
      view: "docs",
      icon: BookOpen,
      label: t("nav.documentation"),
      hint: t("home.docsHint"),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 py-10 sm:px-6">
      {/* Greeting / hero */}
      <section className="mb-8">
        <Badge className="mb-3">{t("app.tagline")}</Badge>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          {user ? t("home.welcome", { name: user.firstName || user.username }) : t("home.welcomeGuest")}
        </h1>
        <p className="mt-2 max-w-xl text-muted-foreground">{t("home.subtitle")}</p>
        {!user && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => openAuth("login")}
              className="inline-flex h-10 items-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
            >
              {t("common.signin")}
            </button>
            <button
              onClick={() => openAuth("register")}
              className="inline-flex h-10 items-center rounded-md border bg-card px-5 text-sm font-medium transition-colors hover:bg-muted"
            >
              {t("common.signup")}
            </button>
          </div>
        )}
      </section>

      {/* Destination cards = the menu */}
      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c) => {
          const Icon = c.icon;
          const isDocs = c.view === "docs";
          const inner = (
            <>
              <div className="flex items-start justify-between gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-muted">
                  <Icon className="h-5 w-5" />
                </span>
                {c.badge ? (
                  <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
                    {c.badge}
                  </Badge>
                ) : (
                  <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                )}
              </div>
              <div className="mt-3 font-semibold">{c.label}</div>
              <div className="mt-0.5 text-sm text-muted-foreground">{c.hint}</div>
            </>
          );
          const cls =
            "group rounded-2xl border bg-card p-5 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-2";
          return isDocs ? (
            <a key="docs" href="/documentation" className={cls}>
              {inner}
            </a>
          ) : (
            <button key={c.view} onClick={() => openArea(c.view as ViewName)} className={cls}>
              {inner}
            </button>
          );
        })}
      </section>

      {impersonating && (
        <p className="mt-6 rounded-xl border border-amber-300/60 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-300">
          {t("preview.bannerNote")}
        </p>
      )}
    </div>
  );
}
